<template>
  <div class="white-bg p2 m3">
    <div v-for="(line, lineIndex) in form" :key="lineIndex" class="flex flex-wrap inputs-holder">
      <div v-for="(column, columnIndex) in line" :key="columnIndex" class="relative px1" :class="column.class">
        <!-- Simple Field -->
        <template v-if="simpleField.includes(column.type)">
          <label class="h6n bold" :for="column.id">{{ column.label }}<span v-if="column.required" class="red"> *</span></label>
          <div class="relative col-12 mb1">
            <input
              v-if="column.id == 'birth_date'"
              :id="column.id"
              v-model="data[column.id]"
              v-mask="'##/##/####'"
              type="text"
              :disabled="column.disabled || disabledFields"
              class="border col-12 pl2 input-height"
              :class="{'invalid-field': allFieldsValid[column.id]}"
              :placeholder="column.placeholder"
            >
            <input
              v-else
              :id="column.id"
              :ref="column.id"
              v-model="data[column.id]"
              :type="column.type"
              :disabled="column.disabled || disabledFields"
              class="border col-12 pl2 input-height"
              :class="{'invalid-field': allFieldsValid[column.id]}"
              :placeholder="column.placeholder"
            >
          </div>
        </template>

        <template v-else-if="column.type === 'tel'">
          <label class="h6n bold" :for="column.id">{{ column.label }}<span v-if="column.required" class="red"> *</span></label>
          <div class="relative col-12 mb1">
            <vue-tel-input
              :id="column.id"
              :ref="column.id"
              v-model="data[column.id]"
              default-country="FR"
              mode="international"
              :preferred-countries="['FR']"
              :type="column.type"
              :disabled="column.disabled || disabledFields"
              class="border col-12 pl2 input-height"
              :class="{'invalid-field': allFieldsValid[column.id]}"
              :placeholder="column.placeholder"
              @validate="handleValidation"
            />
          </div>
        </template>

        <!-- Select Field -->
        <template v-else-if="column.type === 'select'">
          <label class="h6n bold" :for="column.id">{{ column.label }}<span v-if="column.required" class="red"> *</span></label>
          <div class="relative border white-bg col-12 mb1">
            <select :ref="column.id" v-model="data[column.id]" class="border-none relative z2 col-12 pl2 input-height" :class="{'select-disabled': column.disabled}">
              <option v-if="column.placeholder" :value="null" disabled selected>{{ column.placeholder }}</option>
              <option v-for="(option, optionIndex) in column.options" :key="optionIndex" :disabled="column.disabled || disabledFields" :value="option.value">{{ option.text }}</option>
            </select>
            <span class="absolute red right-0 flex items-center mr1 top-0 bottom-0">
              <i class="icon icon-chevron-down"></i>
            </span>
          </div>
        </template>

        <!-- Info Label -->
        <template v-else-if="column.type === 'info'">
          <p :id="column.id" class="center col-12" style="padding: .625rem 1.875rem; font-size: .8125rem; background-color: #e6ecf2;">{{ column.label }}</p>
        </template>

        <!-- Radio Group -->
        <template v-else-if="column.type === 'radio'">
          <label class="h6n bold" :for="column.id">{{ column.label }}<span v-if="column.required" class="red"> *</span></label>
          <div class="col-12">
            <div v-for="(option, optionIndex) in column.options" :key="optionIndex" class="mr1 inline-block">
              <input :id="column.id + option.value" v-model="data[column.id]" type="radio" :name="column.id" :disabled="column.disabled || disabledFields" :value="option.value">
              <label :class="column.disabled ? 'disabled' : 'pointer'" :for="column.id + option.value">{{ option.text }}</label>
            </div>
          </div>
        </template>

        <!-- Checkbox -->
        <template v-else-if="column.type === 'checkbox'">
          <input :id="column.id" v-model="data[column.id]" type="checkbox" :name="column.id" :disabled="column.disabled || disabledFields">
          <label class="h6n" :for="column.id">{{ column.label }}<span v-if="column.required" class="red"> *</span></label>
        </template>

        <!-- Link -->
        <template v-else-if="column.type === 'link'">
          <div v-if="!column.disabled" class="col-12">
            <span class="pointer" :class="column.class" @click="column.target">{{ column.label }}<span v-if="column.required" class="red"> *</span></span>
          </div>
        </template>
        <p v-if="allFieldsValid[column.id]" class="error-message warning mb2">{{ allFieldsValid[column.id] }}</p>
      </div>
    </div>
    <div class="col-12 flex flex-wrap py2 justify-center">
      <Button v-if="disabledFields" text="Modifier" class="col-12 sm-col-6 p1" btn-color="white" :action="activateEdit" />
      <Button v-if="!disabledFields" text="Annuler" class="col-12 sm-col-6 p1" btn-color="white" :action="goBack.bind(this, route)" />
      <Button v-if="!disabledFields" text="Enregistrer" class="col-12 sm-col-6 p1" :disabled="!isObjectEmpty(allFieldsValid) || !formChanged" button-color="red-bg" :action="saveAction" />
    </div>
  </div>
</template>

<script>

import { parse, isValid, isWithinInterval, startOfDay, addDays, subYears } from 'date-fns';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { VueTelInput } from 'vue-tel-input';
import Button from '@/components/elements/Button';
import { goBack, isObjectEmpty, matchExact, lengthBetween } from '@/mixins/utils';

import 'vue-tel-input/dist/vue-tel-input.css';

export default {
  components: { Button, VueTelInput },
  props: {
    form: {
      type: Array,
      default: []
    },
    data: {
      type: Object,
      default: {}
    },
    saveAction: {
      type: Function,
      default: () => { console.log('It works'); }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup (props) {
    const route = useRoute();
    const startData = { ...props.data };
    const mobileValidation = ref({});

    const formChanged = computed(() => JSON.stringify(startData) !== JSON.stringify(props.data));

    function handleValidation ($event) {
      mobileValidation.value = $event;
    }

    const disabledFields = ref(props.disabled);

    const activateEdit = () => disabledFields.value = false;

    const simpleField = ['text', 'email', 'number', 'password', 'date', 'search', 'url', 'time', 'month', 'week'];
    const allFieldsValid = computed(() => {
      const errors = {};

      props.form.map(line => {
        line.map(column => {
          // Ignore info types
          if (['info', 'link'].includes(column.type)) {
            return;
          }

          if (column.required && props.data[column.id] === null || column.required && props.data[column.id] === undefined) { // When we open the form, the value is undefined or null, we put an global error and not the error in the field itself
            errors.firstTime = true;
            return;
          }

          // Global validations
          const globalValidation = checkError(props.data[column.id], column.id, mobileValidation.value.valid, column.required);
          if (globalValidation) {
            errors[column.id] = globalValidation;
          }
        });
      });
      return errors;
    });

    return { goBack, route, allFieldsValid, isObjectEmpty, simpleField, formChanged, handleValidation, mobileValidation, disabledFields, activateEdit };
  }
};

function checkError (value, type, mobileValidation = false, required = false) {
  let birth_date;
  const dateInstance = new Date();

  // Not required and doesn't have value
  if (!required && !value) {
    return null;
  }

  switch (type) {
    case 'gender':
      // return !value ? 'Veuillez renseigner la civilité' : false;
      return !value ? '' : null;

    case 'firstname':
      if (required && value === '') {
        return 'Veuillez renseigner votre prénom';
      }

      if (value && value.length < 2) {
        return 'Le prénom doit contenir au moins 2 caractères';
      }

      return !matchExact(/^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value) ? 'Un prénom ne peut être composé que de lettres et des caractères " - \'' : false;

    case 'lastname':
      if (required && value === '') {
        return 'Veuillez renseigner votre nom';
      }

      if (value && value.length < 2) {
        return 'Le nom doit contenir au moins 2 caractères';
      }

      return !matchExact(/^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value) ? 'Un nom ne peut être composé que de lettres et des caractères " - \'' : false;

    case 'birth_date':
      if (required && value === '') {
        return 'Veuillez renseigner une date de naissance';
      }

      birth_date = parse(value, 'dd/MM/yyyy', startOfDay(dateInstance));

      if (!isValid(birth_date)) {
        return 'Merci de respecter le format jj/mm/aaaa';
      }

      if (!isWithinInterval(birth_date, { start: addDays(subYears(startOfDay(dateInstance), 100), 1), end: startOfDay(dateInstance) })) {
        return 'La date de naissance est incorrecte';
      }

      return false;

    case 'email':
      if (required && value === '') {
        return "L'email doit être renseigné";
      }

      return !matchExact(/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/, value) ? 'L\'email doit comprendre une @, ainsi qu\'un nom de domaine (.com, .fr, ...)' : false;

    case 'mobilephone':
      if (required && value === '') {
        return 'Veuillez renseigner un numéro';
      }

      if (!matchExact(/^([0-9+\s]{8,})$/, value)) {
        return 'Le numéro ne doit être composé que de chiffres.';
      }
      return !mobileValidation ? 'Veuillez renseigner un numéro valide' : false;

    case 'town':
      if (required && value === '') {
        return 'Veuillez renseigner la ville';
      }

      if (!matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value)) {
        return 'Une ville ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 1, 30) ? 'La ville doit avoir entre 1 et 30 caractères' : null;

    case 'street_name':
      if (required && value === '') {
        return 'Veuillez renseigner un nom de rue';
      }

      if (!matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value)) {
        return 'Une ville ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 1, 30) ? 'La ville doit avoir entre 1 et 30 caractères' : null;

    case 'street_number':
      if (required && value === '') {
        return 'Veuillez renseigner un numéro de rue';
      }

      return !matchExact(/^[a-zA-Z0-9 ]*$/, value) ? 'Le nº de rue doit être composé uniquement de chiffres' : null;

    case 'address1':
      if (required && value === '') {
        return "Veuillez renseigner l'adresse";
      }

      if (!matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value)) {
        return 'Une adresse ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 1, 30) ? "L'adresse postale doit avoir entre 1 et 30 caractères" : null;

    case 'address2':
      if (required && value === '') {
        return "Veuillez renseigner un complément d'adresse";
      }

      if (!matchExact(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/, value)) {
        return 'Une adresse ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 0, 30) ? "L'adresse postale doit avoir entre 0 et 30 caractères" : null;

    case 'zip_code':
      if (required && value === '') {
        return 'Le code postal doit être renseigné';
      }

      return !matchExact(/^([A-Z a-z0-9]){3,10}$/, value) ? 'Le code postal doit être composé uniquement de chiffres' : null;

    case 'country':
      return !value ? 'Veuillez compléter votre pays' : null;

    default:
  }

  return null;
}
</script>

<style>
.invalid-field {
  border: 1px solid red;
}
</style>
